import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { formatZodError } from "@Simplicity-Education/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  sonnerToast,
} from "@simplicity/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

export interface DuplicateSource {
  sparkDashboardId: string;
  title: string;
}

interface DuplicateResponse {
  id: string;
  title: string;
  slug: string;
}

function duplicateSparkDashboard(
  tenantId: string,
  sparkDashboardId: string,
  title: string,
): Promise<DuplicateResponse> {
  return portalFetch<DuplicateResponse>(
    `/api/bi/tenants/${tenantId}/dashboards/${sparkDashboardId}/duplicate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, id: sparkDashboardId, title: title.trim() }),
    },
  );
}

function DuplicateDashboardForm({
  source,
  onClose,
}: {
  source: DuplicateSource;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantPath, currentTenantId } = useTenant();
  const [title, setTitle] = useState(`Copy of ${source.title}`);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const canSubmit = title.trim().length > 0;

  const handleDuplicate = async () => {
    if (!currentTenantId || !canSubmit) return;
    setIsDuplicating(true);
    try {
      const copy = await duplicateSparkDashboard(currentTenantId, source.sparkDashboardId, title);
      void queryClient.invalidateQueries({ queryKey: ["portal-dashboards"] });
      onClose();
      void navigate(tenantPath(`/spark/${copy.id}?edit=1`));
    } catch (err) {
      sonnerToast.error("Duplicate failed", {
        description: formatZodError(err, { unknownFallback: "Could not duplicate the dashboard." }),
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Duplicate dashboard</DialogTitle>
        <DialogDescription>
          The copy keeps the layout, unpublished changes and Properties. It is shared with no one
          until you assign it.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-1 py-2">
        <Label htmlFor="duplicate-title" className="text-xs">
          Title
        </Label>
        <Input
          id="duplicate-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isDuplicating}>
          Cancel
        </Button>
        <Button onClick={() => void handleDuplicate()} disabled={!canSubmit || isDuplicating}>
          {isDuplicating ? "Duplicating..." : "Duplicate"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** Open while `source` is set. Keyed on the source so each open starts from a fresh title. */
export function DuplicateDashboardDialog({
  source,
  onClose,
}: {
  source: DuplicateSource | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={source !== null} onOpenChange={(open) => !open && onClose()}>
      {source && (
        <DuplicateDashboardForm key={source.sparkDashboardId} source={source} onClose={onClose} />
      )}
    </Dialog>
  );
}
